import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import {
  AppRatingService,
  AppRoutes,
  DateFormats,
  DrillScoring,
  formatDateToString,
  PartnersService,
  RatingPromptSheetComponent
} from '@core';
import {
  SzAppShellComponent,
  SzButtonComponent,
  SzCardComponent,
  SzCtaBarComponent,
  SzIcCircleComponent,
  SzIconComponent,
  SzNumBigComponent,
  SzPageTitleComponent,
  SzPartnerCardComponent,
  SzRowCardComponent,
  SzSectionHeadComponent,
  SzSkeletonComponent,
  SzTopBarComponent,
  SzTypePillComponent
} from '@ui';
import { drillScoringShortMapper } from '@drills';
import { SessionRepListComponent } from '../session-rep-list/session-rep-list.component';
import { buildRunRows, formatBestValue } from '../session-rep-list/run-rows.util';
import { SessionRunRow } from '../session-rep-list/session-rep-list.types';
import { SessionDetailQueryHandler } from '../../application/handlers/query/session-detail/session-detail.query-handler';
import { SessionDetailQueryResult } from '../../application/handlers/query/session-detail/session-detail.query-result';
import { SessionDrillViewModel, SessionViewModel } from '../../application/models';

const SECONDS_IN_MINUTE: number = 60;

interface PerDrillRow {
  readonly id: string;
  readonly drillName: string;
  readonly scoringShort: string;
  readonly runsCount: number;
  readonly bestValue: string;
  readonly showBestLabel: boolean;
  readonly runRows: readonly SessionRunRow[];
}

@Component({
  selector: 'lib-session-summary',
  templateUrl: './session-summary.component.html',
  styleUrls: ['./session-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzNumBigComponent,
    SzRowCardComponent,
    SzIcCircleComponent,
    SzTypePillComponent,
    SzSectionHeadComponent,
    SzSkeletonComponent,
    SzCardComponent,
    SzCtaBarComponent,
    SzButtonComponent,
    SzIconComponent,
    SzPartnerCardComponent,
    SessionRepListComponent,
    RatingPromptSheetComponent
  ],
  providers: [SessionDetailQueryHandler]
})
export class SessionSummaryComponent implements OnInit {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly detailHandler: SessionDetailQueryHandler = inject(SessionDetailQueryHandler);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly ratingService: AppRatingService = inject(AppRatingService);
  private readonly partners: PartnersService = inject(PartnersService);

  readonly partnerLogo: string = this.partners.sejfy.logoSrc;
  readonly partnerName: string = this.partners.sejfy.name;
  readonly partnerAccent: string = this.partners.sejfy.accentColor;

  readonly completed: boolean = this.route.snapshot.queryParamMap.get('completed') === 'true';

  readonly session: WritableSignal<SessionViewModel | null> = signal<SessionViewModel | null>(null);

  // Losowy tytuł podsumowania - wybieramy raz, a po zmianie języka dzielimy go ponownie.
  private titleIndex: number | null = null;
  private readonly titlePhrase: WritableSignal<{ main: string; accent: string }> = signal({
    main: '',
    accent: ''
  });

  readonly titleMain: Signal<string> = computed(() => this.titlePhrase().main);
  readonly titleAccent: Signal<string> = computed(() => this.titlePhrase().accent);

  readonly endedAtKicker: Signal<string> = computed(() => {
    const s: SessionViewModel | null = this.session();
    if (s === null) return '';
    return `${formatDateToString(s.endedAt, DateFormats.DayMonthNameLong)} · ${formatDateToString(s.endedAt, DateFormats.Time)}`;
  });

  readonly durationMinutes: Signal<string> = computed(() => {
    const s: SessionViewModel | null = this.session();
    if (s === null) return '00';
    return Math.floor(s.durationSeconds / SECONDS_IN_MINUTE)
      .toString()
      .padStart(2, '0');
  });

  readonly totalRunsLabel: Signal<string> = computed(() =>
    (this.session()?.totalRuns ?? 0).toString().padStart(2, '0')
  );

  readonly drillCountLabel: Signal<string> = computed(() =>
    (this.session()?.sessionDrills.length ?? 0).toString().padStart(2, '0')
  );

  readonly perDrillRows: Signal<readonly PerDrillRow[]> = computed((): readonly PerDrillRow[] => {
    const s: SessionViewModel | null = this.session();
    if (s === null) return [];
    const repsUnit: string = this.translate.instant('session.numpad.repsUnit');
    return s.sessionDrills.map(
      (sd: SessionDrillViewModel, i: number): PerDrillRow => ({
        id: `${sd.drillId ?? 'pos'}-${i}`,
        drillName: sd.drillName,
        scoringShort: drillScoringShortMapper(sd.scoring),
        runsCount: sd.runs.length,
        bestValue: formatBestValue(sd, repsUnit),
        showBestLabel: sd.scoring !== DrillScoring.RepsOnly,
        runRows: buildRunRows(sd, repsUnit)
      })
    );
  });

  // Zbior ID rozwinietych drilli - accordion multi-open (kilka moze byc otwartych naraz).
  private readonly expandedIds: WritableSignal<ReadonlySet<string>> = signal<ReadonlySet<string>>(new Set());

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  toggleDrill(id: string): void {
    this.expandedIds.update((current: ReadonlySet<string>): ReadonlySet<string> => {
      const next: Set<string> = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  ngOnInit(): void {
    this.translate
      .stream('session.summary.titles')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((titles: unknown) => this.applyTitle(titles));

    const id: string | null = this.route.snapshot.paramMap.get('id');
    if (id === null) {
      void this.router.navigate(['/', AppRoutes.DRILLS]);
      return;
    }
    this.detailHandler
      .query(id)
      .pipe(take(1))
      .subscribe((r: SessionDetailQueryResult) => this.session.set(r.session));

    // Po ukończonej sesji (nie przy podglądzie z historii) - policz sesję i ew.
    // pokaż prompt oceny aplikacji. Gating w AppRatingService. ADR-0011.
    if (this.completed) {
      this.ratingService.registerCompletedSessionAndMaybePrompt();
    }
  }

  openPartner(): void {
    void this.router.navigateByUrl(`/${AppRoutes.PARTNERS}/${this.partners.sejfy.id}`);
  }

  done(): void {
    if (this.session()?.plan) {
      void this.router.navigate(['/', AppRoutes.PLAN], { queryParams: { tab: 'completed' } });
      return;
    }
    void this.router.navigate(['/', AppRoutes.DRILLS]);
  }

  // Wybiera losowy tytuł (raz) i dzieli go na pierwsze słowo (main) oraz resztę (accent).
  private applyTitle(titles: unknown): void {
    if (!Array.isArray(titles) || titles.length === 0) {
      // Fallback na statyczny tytuł, gdy lista nie jest jeszcze załadowana.
      this.titlePhrase.set({
        main: this.translate.instant('session.summary.titleMain'),
        accent: this.translate.instant('session.summary.titleAccent')
      });
      return;
    }
    if (this.titleIndex === null) {
      this.titleIndex = Math.floor(Math.random() * titles.length);
    }
    const phrase: string = (titles[this.titleIndex] ?? titles[0]) as string;
    const spaceIndex: number = phrase.indexOf(' ');
    this.titlePhrase.set(
      spaceIndex === -1
        ? { main: phrase, accent: '' }
        : { main: phrase.slice(0, spaceIndex), accent: phrase.slice(spaceIndex + 1) }
    );
  }
}
